import React, { memo, useEffect, useState } from 'react';
import {
  ActivityIndicator, Image, Pressable, StyleSheet, Text, View,
} from 'react-native';
import Colors from '../../constants/Colors';
import OrionArrowIcon from '../24pxIcons/OrionArrowIcon';
import CheckBox24 from '../24pxIcons/CheckBox24';
import { AssetInstance } from '../../constants/Types';

const Placeholder = require('../../assets/images/placeholder.png');

interface AssetRowMobileProps {
  selected: boolean,
  selectAsset: () => void,
  asset: AssetInstance,
  selectionChanged: () => void,
}

const AssetRowMobile = (props: AssetRowMobileProps) => {
  if (!props.asset) return null;

  const buildImageUri = (url: string): string => {
    if (!/^https?:\/\//i.test(url)) return url;
    const cacheParam = url.includes('?') ? '&' : '?';
    return `${url}${cacheParam}time=${Date.now()}`;
  };

  const [selected, setSelected] = useState<boolean>(props.selected ?? false);
  const [switchToggled] = useState<boolean>(true);
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [lastValidImageUri, setLastValidImageUri] = useState<string | null>(null);
  const [loadingImage, setLoadingImage] = useState<boolean>(true);

  const deleteEnabled: boolean = false;

  useEffect(() => {
    if (props?.asset?.imageUrl?.length > 0) {
      const nextImage = buildImageUri(props.asset.imageUrl);
      setImageUri(nextImage);
      setLoadingImage(true);
      return;
    }
    setImageUri(null);
    setLastValidImageUri(null);
    setLoadingImage(false);
  }, [props.asset]);

  const renderCardClosedView = () => (
    <View
      style={styles.cardClosed}
      role='link'
    >
      <View style={[styles.cardClosedContent, switchToggled && styles.cardClosedEnabled]} />
      {deleteEnabled && <Pressable
        style={{ justifyContent: 'center' }}
        onPress={() => {
          props.selectionChanged();
          setSelected(!selected);
        }}
      >
        <CheckBox24
          checkcolor={props.selected ?? selected ? Colors.light.hereworksBlue500 : null}
          color={props.selected ?? selected
            ? Colors.light.hereworksBlue500 : Colors.light.hereworksBlue100}
          style={{ marginHorizontal: 16 }}
        />
      </Pressable>}
      <View style={styles.imageContainer}>
        <Image
          source={imageUri ? { uri: imageUri } : Placeholder}
          resizeMode='cover'
          onLoad={() => {
            if (imageUri) setLastValidImageUri(imageUri);
          }}
          onLoadEnd={() => setLoadingImage(false)}
          onError={() => {
            if (lastValidImageUri && lastValidImageUri !== imageUri) {
              setImageUri(lastValidImageUri);
              return;
            }
            setLoadingImage(false);
          }}
          style={styles.image}
        />
        {imageUri && loadingImage && (
          <ActivityIndicator style={{ position: 'absolute' }} />
        )}
      </View>
      <View style={styles.nameContainer}>
        <Text style={styles.name}>{props.asset.name}</Text>
      </View>
    </View>
  );

  return (
    <Pressable
      style={({ pressed }) => [
        pressed
          ? { backgroundColor: Colors.light.hereworksWhite300 }
          : { backgroundColor: Colors.light.hereworksWhite100 },
        styles.container,
      ]}
      onPress={props.selectAsset}
    >
      {renderCardClosedView()}
      <OrionArrowIcon style={styles.arrowIcon} />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  nameContainer: {
    flex: 1,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  name: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 14,
    color: Colors.light.hereworksBlack600,
    textAlign: 'left',
    marginLeft: 12,
  },
  container: {
    borderRadius: 10,
    overflow: 'hidden',
    alignSelf: 'stretch',
    marginVertical: 6,
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardClosed: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  cardClosedContent: {
    width: 8,
    height: 60,
    zIndex: 1,
    backgroundColor: Colors.light.hereworksWhite600,
  },
  cardClosedEnabled: {
    backgroundColor: Colors.light.hereworksGreen400,
  },
  arrowIcon: {
    transform: [{ rotateZ: '180deg' }],
    marginEnd: 6,
  },
  imageContainer: {
    marginEnd: 12,
    marginStart: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: 80,
    height: 48,
    borderRadius: 4,
  },
});

export default memo(AssetRowMobile);
